import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { useAuthentication } from '@auth/hooks/useAuthentication'
import { getAccountInfo } from '@features/account/services/MkApiAccount'
import { IOwner } from '@features/account/types'

export interface UseProfileReturn {
  profileData: IOwner | null
  profileLoading: boolean
  profileError: string | null
  /**
   * Force a fresh GET of the account profile and resolve with the new owner (or `null` if the
   * fetch failed). Callers like the My Account edit form rely on the returned value to verify
   * that a save actually persisted before showing a success snackbar — see EditInfoForm.
   */
  refreshProfile: () => Promise<IOwner | null>
  resetProfile: () => void
}

export const useProfile = (): UseProfileReturn => {
  const [profileData, setProfileData] = useState<IOwner | null>(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [profileError, setProfileError] = useState<string | null>(null)
  const [fetchState, setFetchState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  const { isAuthorized, session } = useAuthentication()
  const authHeaders = useAuthHeaders({ session: session || null })

  const hasFetchedRef = useRef(false)
  const jwtTokenRef = useRef<string | undefined>()
  const currentRequestRef = useRef<Promise<IOwner | null> | null>(null)
  const lastDataRef = useRef<IOwner | null>(null)

  const fetchProfile = useCallback(async (): Promise<IOwner | null> => {
    if (!isAuthorized) {
      setProfileError('User not authorized')
      return null
    }

    // If we already have a request in progress, wait for it and reuse its result.
    if (currentRequestRef.current) {
      return currentRequestRef.current
    }

    // If we've already fetched with this JWT token, don't fetch again — return the cached owner.
    const currentJwtToken = authHeaders?.['X-JWT-Token']
    if (hasFetchedRef.current && jwtTokenRef.current === currentJwtToken) {
      return lastDataRef.current
    }

    setProfileLoading(true)
    setFetchState('loading')
    setProfileError(null)

    const requestPromise: Promise<IOwner | null> = (async () => {
      try {
        const data = await getAccountInfo(authHeaders)

        if (data) {
          setProfileData(data)
          lastDataRef.current = data
          setFetchState('success')
          hasFetchedRef.current = true
          jwtTokenRef.current = currentJwtToken
          return data
        }
        setProfileError('Failed to fetch profile data')
        setFetchState('error')
        return null
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch profile data'
        setProfileError(errorMessage)
        setFetchState('error')
        return null
      } finally {
        setProfileLoading(false)
        currentRequestRef.current = null
      }
    })()

    currentRequestRef.current = requestPromise
    return requestPromise
  }, [authHeaders, isAuthorized])

  const refreshProfile = useCallback((): Promise<IOwner | null> => {
    hasFetchedRef.current = false
    jwtTokenRef.current = undefined
    currentRequestRef.current = null
    return fetchProfile()
  }, [fetchProfile])

  const resetProfile = useCallback(() => {
    setProfileData(null)
    lastDataRef.current = null
    setProfileLoading(false)
    setProfileError(null)
    setFetchState('idle')
    hasFetchedRef.current = false
    jwtTokenRef.current = undefined
    currentRequestRef.current = null
  }, [])

  useEffect(() => {
    // Only fetch if user is authorized and we haven't fetched yet, or if the JWT token changed
    if (isAuthorized && (!hasFetchedRef.current || jwtTokenRef.current !== authHeaders?.['X-JWT-Token'])) {
      fetchProfile()
    }

    // Reset data if user is not authorized
    if (!isAuthorized) {
      resetProfile()
    }
  }, [isAuthorized, authHeaders?.['X-JWT-Token'], fetchProfile, resetProfile])

  return {
    profileData,
    profileLoading,
    profileError,
    refreshProfile,
    resetProfile,
  }
}
